import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { fetchClientCoachAccess } from "../lib/stripe.js";
import { toUserFacingBillingError, billingErrorCopy } from "../lib/userFacingErrors.js";
import { useAuthUserId } from "../hooks/useAuthUserId.js";
import { useRole } from "../hooks/useRole.js";

const ClientCoachAccessContext = createContext(null);

export function ClientCoachAccessProvider({ children }) {
  const { userId, authLoading } = useAuthUserId();
  const role = useRole();
  const [access, setAccess] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const refetch = useCallback(async ({ silent = false } = {}) => {
    if (!userId) {
      setAccess(null);
      setIsLoading(false);
      setError(null);
      return;
    }
    if (!silent) setIsLoading(true);
    setError(null);
    try {
      const data = await fetchClientCoachAccess();
      setAccess(data);
    } catch (e) {
      setError(
        toUserFacingBillingError(e?.message ?? String(e), {
          fallback: billingErrorCopy.verifyClientPlan,
        }),
      );
      setAccess(null);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (authLoading || role === "loading") return;
    if (!userId || role === "coach") {
      setAccess(null);
      setError(null);
      setIsLoading(false);
      return;
    }
    refetch();
  }, [userId, authLoading, role, refetch]);

  const value = useMemo(
    () => ({
      access,
      accessGranted: Boolean(access?.accessGranted),
      hasCoach: Boolean(String(access?.coachId ?? "").trim()),
      isLoading,
      error,
      refetch,
    }),
    [access, isLoading, error, refetch],
  );

  return (
    <ClientCoachAccessContext.Provider value={value}>
      {children}
    </ClientCoachAccessContext.Provider>
  );
}

export function useClientCoachAccess() {
  const ctx = useContext(ClientCoachAccessContext);
  if (!ctx) {
    throw new Error("useClientCoachAccess must be used within ClientCoachAccessProvider");
  }
  return ctx;
}
